import ScrollReveal from '@/components/ScrollReveal';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import beach from '@/assets/beach.jpg';
import boatTrip from '@/assets/boat-trip.jpg';
import harbour from '@/assets/harbour.jpg';
import sivotaAerial from '@/assets/sivota-aerial.jpg';
import terraceDining from '@/assets/terrace-dining.jpg';
import corfu from '@/assets/corfu.jpg';
import paxos from '@/assets/paxos.jpg';
import antipaxos from '@/assets/antipaxos.jpg';

export default function Destinations() {
  const { t } = useLanguage();

  const destinations = [
    { title: t('secludedBeaches'), desc: t('secludedBeachesDesc'), image: beach, location: t('sivotaCoastline') },
    { title: t('boatTrips'), desc: t('boatTripsDesc'), image: boatTrip, location: t('ionianSea') },
    { title: t('sivotaHarbour'), desc: t('sivotaHarbourDesc'), image: harbour, location: t('sivotaVillage') },
    { title: t('localDining'), desc: t('localDiningDesc'), image: terraceDining, location: t('sivotaSurrounds') },
  ];

  const islands = [
    { name: 'Corfu', desc: t('corfuDesc'), img: corfu, time: t('corfuTime') },
    { name: 'Paxos', desc: t('paxosDesc'), img: paxos, time: t('paxosTime') },
    { name: 'Antipaxos', desc: t('antipaxosDesc'), img: antipaxos, time: t('antipaxosTime') },
  ];

  return (
    <div>
      <section className="relative h-[80vh] flex items-end overflow-hidden">
        <img src={sivotaAerial} alt="Aerial view of Sivota coastline" className="absolute inset-0 w-full h-full object-cover hero-image-bright" width={1920} height={1080} fetchPriority="high" style={{ animation: 'heroZoom 25s ease-out forwards' }} />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative z-10 section-padding pb-16 lg:pb-24 w-full max-w-3xl">
          <p className="text-white/80 tracking-[0.4em] uppercase text-xs font-sans mb-4 drop-shadow-md">{t('explore')}</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light text-white drop-shadow-lg">{t('destinations')}</h1>
          <p className="text-white/80 text-lg mt-4 max-w-lg drop-shadow-md">{t('hiddenCovesDesc')}</p>
        </div>
      </section>

      <section className="section-padding py-20 lg:py-28">
        <ScrollReveal>
          <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto">{t('destinationsIntro')}</p>
        </ScrollReveal>
      </section>

      <section className="section-padding pb-16">
        <div className="space-y-6 max-w-7xl mx-auto">
          {destinations.map((d, i) => (
            <ScrollReveal key={i}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch rounded-sm overflow-hidden">
                <div className={`image-zoom aspect-[4/3] lg:aspect-auto lg:min-h-[450px] ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img src={d.image} alt={d.title} className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
                </div>
                <div className={`bg-secondary flex flex-col justify-center p-10 lg:p-16 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <p className="text-luxury mb-4">{d.location}</p>
                  <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-6">{d.title}</h2>
                  <p className="text-muted-foreground leading-relaxed max-w-md">{d.desc}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section className="section-padding py-24 lg:py-36 bg-secondary">
        <ScrollReveal>
          <div className="text-center mb-14">
            <p className="text-luxury mb-4">{t('islandHopping')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-4">{t('jewelsOfIonian')}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">{t('islandHoppingIntro')}</p>
          </div>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {islands.map((d, i) => (
            <ScrollReveal key={i} delay={i * 0.12}>
              <div className="group relative rounded-sm overflow-hidden aspect-[3/4]">
                <img src={d.img} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" width={600} height={800} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                  <p className="text-white/80 text-xs tracking-[0.2em] uppercase mb-2">{d.time}</p>
                  <h3 className="font-serif text-3xl lg:text-4xl text-white mb-2">{d.name}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{d.desc}</p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch">
          <ScrollReveal>
            <div className="image-zoom aspect-[4/3] lg:aspect-auto lg:min-h-[500px]">
              <img src={sivotaAerial} alt="Sivota from above" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <div className="bg-card flex flex-col justify-center p-10 lg:p-16 xl:p-20 h-full">
              <p className="text-luxury mb-4">{t('thesprotiaRegion')}</p>
              <h2 className="font-serif text-3xl md:text-4xl font-light text-foreground mb-6">{t('natureHiking')}</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{t('natureHikingDesc1')}</p>
              <p className="text-muted-foreground leading-relaxed">{t('natureHikingDesc2')}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="relative py-28 lg:py-36">
        <img src={paxos} alt="" className="absolute inset-0 w-full h-full object-cover hero-image-bright" loading="lazy" width={1920} height={1080} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10" />
        <div className="relative z-10 text-center section-padding">
          <ScrollReveal>
            <p className="text-white/80 tracking-[0.3em] uppercase text-xs font-sans mb-4">{t('yourGateway')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-white mb-6 drop-shadow-lg">{t('startIonianAdventure')}</h2>
            <p className="text-white/80 text-lg max-w-lg mx-auto mb-10">{t('ionianAdventureDesc')}</p>
            <Link to="/booking"
              className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors">
              {t('checkAvailability')}
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
